import { FC, memo, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import LicenseContext from 'src/contexts/LicenseContext';
import PlansContext from 'src/contexts/PlansContext';
import datetimeDecorator from 'src/decorators/datetime.decorator';
import { useConvenienceDispatchDeliveriesQuery } from 'src/hooks/useConvenienceDispatchDeliveries.query';
import { useGaragesQuery } from 'src/hooks/useGarages.query';
import { useQueryPlanningsDrivers } from 'src/hooks/useQueryPlanningsDrivers';
import { useQueryPlanningsTrucks } from 'src/hooks/useQueryPlanningsTrucks';
import { useInfiniteQueryShipmentOrderLegRuns } from 'src/hooks/useQueryShipmentOrderLegRuns';

import { buildDisplayDays, resolveSelectedDay } from './displayDays';
import MapPagePresenter from './Presenter';
import { resolveDeliveryDataDisabledReason } from './resolveMapTariffAvailability';

// 全ページを並列で取り切る。値の意味はカード配車の index.tsx のコメント参照
const PER_PAGE_DELIVERIES = 20;
const PER_PAGE_LEG_RUNS = 200;

const MapPage: FC = memo(() => {
  const { startOn: startOnParam, endOn: endOnParam } = useParams<{ startOn: string; endOn: string }>();
  const navigate = useNavigate();
  const licenseContext = useContext(LicenseContext);
  const plansContext = useContext(PlansContext);

  useEffect(() => {
    if (startOnParam && endOnParam) return;
    const on =
      licenseContext.config?.on ??
      localStorage.getItem('planningOn') ??
      datetimeDecorator.toYyyyMmDd(new Date());
    void navigate(`/map/${on}/${on}`, { replace: true });
  }, [startOnParam, endOnParam, licenseContext.config?.on, navigate]);

  const startOn = startOnParam ?? '';
  const endOn = endOnParam ?? startOn;

  useEffect(() => {
    if (!startOn || !endOn) return;
    plansContext.setStartOn(startOn);
    plansContext.setEndOn(endOn);
    licenseContext.setStartEndOn({ startOn, endOn });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startOn, endOn]);

  /**
   * 表示日。期間を跨いでも画面は 1 日ぶんしか出さないので、取得もその 1 日に絞る
   * （期間ぶん取ると、見ない日のページを取り続けて初期表示が遅くなる）。
   *
   * 期間そのものは URL に残す（タブと日付ピッカーが要る）。日を切り替えるとクエリキーが
   * 変わるので、一度見た日は react-query のキャッシュから即座に戻る。
   */
  const displayDays = useMemo(() => buildDisplayDays(startOn, endOn), [startOn, endOn]);
  const [touchedDay, setTouchedDay] = useState<string | null>(null);
  // 期間を変えたら先頭の日へ戻す（前の選択が残ると、期間外の日を取りに行く）
  const displayDay = displayDays.length <= 1 ? startOn : resolveSelectedDay(displayDays, touchedDay);

  const deliveriesQuery = useConvenienceDispatchDeliveriesQuery(displayDay, displayDay, null, PER_PAGE_DELIVERIES);
  const legRunsQuery = useInfiniteQueryShipmentOrderLegRuns(displayDay, displayDay, PER_PAGE_LEG_RUNS);
  const trucksQuery = useQueryPlanningsTrucks(displayDay, displayDay);
  const driversQuery = useQueryPlanningsDrivers(displayDay, displayDay);
  // 車庫は /plans と同じ表示にするため、車庫マスタ(GET /api/v3/garages)を取得して突合する。
  const garagesQuery = useGaragesQuery();

  const deliveries = deliveriesQuery.data?.pages.flatMap((p) => p.data) ?? [];
  const legRuns = legRunsQuery.data?.pages.flatMap((p) => p.data) ?? [];

  // 32列フィルタ(未割当リスト)向けの選択肢・準備状態。ConvenienceDispatches/index.tsx と同じ導出。
  const trucksForFilter = useMemo(
    () => (trucksQuery.data ?? []).map((t) => ({ id: t.id, name: t.truckNumber || t.licensePlateValue })),
    [trucksQuery.data],
  );
  const driversForFilter = useMemo(
    () => (driversQuery.data ?? []).map((d) => ({ id: d.id, name: d.name })),
    [driversQuery.data],
  );
  const customInputFieldNames = useMemo(
    () => licenseContext.config?.custom_input_fields ?? [],
    [licenseContext.config],
  );

  // 運賃計算は全ページ取得が終わってから実行させる（段階取得の途中で押されると、
  // まだ届いていない配車が対象から漏れる）。Presenter は query を持たないので、
  // 取得系の判定はここで済ませて理由の文字列 1 本だけを渡す。
  const deliveryDataDisabledReason = resolveDeliveryDataDisabledReason({
    isError: deliveriesQuery.isError,
    isFetching: deliveriesQuery.isFetching,
    hasNextPage: Boolean(deliveriesQuery.hasNextPage),
    isFetchingNextPage: deliveriesQuery.isFetchingNextPage,
  });

  return (
    <MapPagePresenter
      deliveries={deliveries}
      legRuns={legRuns}
      trucks={trucksQuery.data ?? []}
      drivers={driversQuery.data ?? []}
      garageData={garagesQuery.data ?? []}
      isLoading={deliveriesQuery.isLoading || legRunsQuery.isLoading}
      // 取得もキャッシュ操作も自動配車の対象も「表示日」で揃える。ここに期間を渡すと、
      // 画面は 1 日ぶんなのに自動配車や楽観更新が期間ぶんを相手にしてずれる
      startOn={displayDay}
      endOn={displayDay}
      rangeStartOn={startOn}
      rangeEndOn={endOn}
      onDisplayDayChange={setTouchedDay}
      customInputFieldNames={customInputFieldNames}
      useDriverApp={licenseContext.config?.use_driver_app}
      useTruckPosition={licenseContext.config?.use_truck_position}
      trucksForFilter={trucksForFilter}
      driversForFilter={driversForFilter}
      companyId={String(licenseContext.config?.selected_company_id ?? 0)}
      useAlgorithmPlanning={Boolean(licenseContext.config?.use_algorithm_planning)}
      loadAfterInitialStock={Boolean(licenseContext.config?.load_after_initial_stock)}
      setCurrentTimeToAlgorithm={Boolean(licenseContext.config?.set_current_time_to_algorithm)}
      useScenarioPlanning={Boolean(licenseContext.config?.use_scenario_planning)}
      deliveryListGrouping={licenseContext.config?.delivery_list_grouping ?? 'groupByAddress'}
      selectedCompanyId={licenseContext.config ? licenseContext.config.selected_company_id : null}
      isLicenseReady={Boolean(licenseContext.config)}
      isMastersReady={trucksQuery.isSuccess && driversQuery.isSuccess}
      isMastersError={trucksQuery.isError || driversQuery.isError}
      deliveryDataDisabledReason={deliveryDataDisabledReason}
      deliveriesHasNextPage={Boolean(deliveriesQuery.hasNextPage)}
      onNavigate={(path) => { void navigate(path); }}
      // `/plans` と同じく URL を書き換える（ActionsPresenter.tsx:115-125）。
      // replace を付けないので戻るボタンで前の日付へ戻れる。日付が無いときの
      // 自動補完（:30）だけが replace: true なのは、そちらが履歴に残す価値の
      // 無いリダイレクトのため。
      onDateRangeChange={(newStartOn, newEndOn) => {
        void navigate(`/map/${newStartOn}/${newEndOn}`);
      }}
    />
  );
});

export default MapPage;
